import { Pause, Play, StepBack, StepForward, X } from "lucide-react";
import { PLAYBACK_SPEEDS } from "utils/capturePlayback";
import { fieldTime } from "utils/fieldFormat";

// The capture slider and its playback controls. Presentational: the page owns the selection,
// playback and range (as resolved indices). In fullscreen the same elements form the slim bar
// under the viewer (see .field-stage.is-fullscreen in field.css).
export default function CaptureTransport({
  captures,
  index,
  total,
  onSeek,
  playing,
  canPlay,
  onTogglePlay,
  onStep,
  fps,
  onFps,
  range,
  onMark,
  onClearRange,
}) {
  const count = captures.length;
  const capture = captures[index];
  const at = (i) => (count > 1 ? i / (count - 1) : 0);
  return (
    <div className="field-transport">
      <label
        className="field-metric-label field-transport-label"
        htmlFor="capture-timeline"
      >
        Capture history · {count} loaded of {total || count} captures
      </label>
      <div className="field-scrubber">
        {range && count > 1 && (
          <span
            className="field-range-band"
            data-testid="capture-range"
            aria-hidden="true"
            style={{ "--from": at(range.start), "--to": at(range.end) }}
          />
        )}
        <input
          id="capture-timeline"
          aria-valuetext={
            capture ? `${fieldTime(capture.captured_at)} SGT` : "No capture"
          }
          type="range"
          min={0}
          max={Math.max(0, count - 1)}
          value={index}
          disabled={count < 2}
          onChange={(e) => onSeek(Number(e.target.value))}
        />
      </div>
      <div className="time-labels">
        <span>
          {count ? fieldTime(captures[0].captured_at) : "Awaiting capture"}
        </span>
        <span className="selected">
          {capture ? `${fieldTime(capture.captured_at)} SGT` : "No capture selected"}
        </span>
        <span>{count ? fieldTime(captures[count - 1].captured_at) : ""}</span>
      </div>
      <div className="field-transport-bar">
        <div className="field-transport-buttons">
          <button
            className="field-button"
            aria-label="Previous capture"
            title="Previous capture (←)"
            disabled={index <= 0 || !count}
            onClick={() => onStep(-1)}
          >
            <StepBack size={14} />
          </button>
          <button
            className="field-button primary field-play"
            title={playing ? "Pause (Space)" : "Play from this capture (Space)"}
            disabled={!playing && !canPlay}
            onClick={onTogglePlay}
          >
            {playing ? <Pause size={14} /> : <Play size={14} />}
            {playing ? "Pause" : "Play"}
          </button>
          <button
            className="field-button"
            aria-label="Next capture"
            title="Next capture (→)"
            disabled={index >= count - 1}
            onClick={() => onStep(1)}
          >
            <StepForward size={14} />
          </button>
        </div>
        <div className="field-segment field-speed" role="group" aria-label="Playback speed">
          {PLAYBACK_SPEEDS.map((n) => (
            <button
              key={n}
              className={fps === n ? "active" : ""}
              aria-pressed={fps === n}
              title={`${n} capture${n === 1 ? "" : "s"} per second`}
              onClick={() => onFps(n)}
            >
              {n} fps
            </button>
          ))}
        </div>
        <div className="field-range-controls">
          <button
            className="field-button"
            title="Mark this capture as the loop start ([)"
            disabled={!capture}
            onClick={() => onMark("start")}
          >
            Set start
          </button>
          <button
            className="field-button"
            title="Mark this capture as the loop end (])"
            disabled={!capture}
            onClick={() => onMark("end")}
          >
            Set end
          </button>
          <button
            className="field-button"
            aria-label="Clear range"
            title="Clear the loop range"
            disabled={!range}
            onClick={onClearRange}
          >
            <X size={14} /> Clear
          </button>
          <span className="field-range-summary">
            {range
              ? `Loop ${fieldTime(captures[range.start].captured_at)} → ${fieldTime(captures[range.end].captured_at)} · ${range.end - range.start + 1} capture${range.end === range.start ? "" : "s"}`
              : "Mark a start and an end to loop playback"}
          </span>
        </div>
      </div>
    </div>
  );
}
